import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiDownload, FiPlay, FiMic, FiMessageSquare, FiCpu } from 'react-icons/fi';

const HeroSection = styled.section`
  id: hero;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8rem 2rem 4rem;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 6rem 1rem 2rem;
    text-align: center;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Content = styled.div`
  z-index: 2;
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(100, 150, 255, 0.1);
  border: 1px solid rgba(100, 150, 255, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6496FF;
  margin-bottom: 2rem;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #FFFFFF 0%, #E8EAF0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const PrimaryButton = styled(motion.button)`
  background: linear-gradient(135deg, #6496FF 0%, #4678FF 100%);
  color: #FFFFFF;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(100, 150, 255, 0.4);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const SecondaryButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  color: #FFFFFF;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const VisualSection = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const AppPreview = styled(motion.div)`
  background: rgba(25, 25, 35, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(20px);
  width: 100%;
  max-width: 500px;
  position: relative;
`;

const AppHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const AppIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #6496FF 0%, #4678FF 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const AppTitle = styled.div`
  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }
  
  p {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.6);
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FeatureItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: rgba(100, 150, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(100, 150, 255, 0.1);
`;

const FeatureIcon = styled.div`
  width: 32px;
  height: 32px;
  background: rgba(100, 150, 255, 0.2);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6496FF;
`;

const FeatureText = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const FloatingIcon = styled(motion.div)`
  position: absolute;
  width: 60px;
  height: 60px;
  background: rgba(100, 150, 255, 0.1);
  border: 1px solid rgba(100, 150, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #6496FF;
  backdrop-filter: blur(10px);
`;

const Hero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      rotate: [0, 5, -5, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <HeroSection id="hero">
      <Container>
        <Content>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Badge variants={itemVariants}>
              <FiCpu />
              Meet Your AI Meeting Copilot
            </Badge>
            
            <Title variants={itemVariants}>
              Real-time intelligence. Instant confidence.
            </Title>
            
            <Subtitle variants={itemVariants}>
              Your AI teammate that sits silently in every meeting — listening, understanding, 
              and ready to answer your questions the moment you need help. No context setup required.
            </Subtitle>
            
            <ButtonGroup variants={itemVariants}>
              <PrimaryButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiDownload />
                Download Now
              </PrimaryButton>
              
              <SecondaryButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiPlay />
                Watch Demo
              </SecondaryButton>
            </ButtonGroup>
          </motion.div>
        </Content>

        <VisualSection>
          <FloatingElements>
            <FloatingIcon
              variants={floatingVariants}
              animate="animate"
              style={{ top: '10%', right: '20%' }}
            >
              🎤
            </FloatingIcon>
            <FloatingIcon
              variants={floatingVariants}
              animate="animate"
              style={{ top: '60%', right: '10%', animationDelay: '2s' }}
            >
              🤖
            </FloatingIcon>
            <FloatingIcon
              variants={floatingVariants}
              animate="animate"
              style={{ bottom: '20%', left: '10%', animationDelay: '4s' }}
            >
              📝
            </FloatingIcon>
          </FloatingElements>

          <AppPreview
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <AppHeader>
              <AppIcon>🧠</AppIcon>
              <AppTitle>
                <h3>Meeting Copilot</h3>
                <p>Your AI Teammate</p>
              </AppTitle>
            </AppHeader>

            <FeatureList>
              <FeatureItem
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
              >
                <FeatureIcon><FiMic /></FeatureIcon>
                <FeatureText>Listens without disrupting</FeatureText>
              </FeatureItem>
              
              <FeatureItem
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0 }}
              >
                <FeatureIcon><FiMessageSquare /></FeatureIcon>
                <FeatureText>Answers instantly</FeatureText>
              </FeatureItem>
              
              <FeatureItem
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2 }}
              >
                <FeatureIcon><FiCpu /></FeatureIcon>
                <FeatureText>Understands context</FeatureText>
              </FeatureItem>
            </FeatureList>
          </AppPreview>
        </VisualSection>
      </Container>
    </HeroSection>
  );
};

export default Hero; 